/**
 * Book a Call — the self-hosted scheduler (replaces the Calendly embed).
 *
 * Renders the BookingWidget directly for a fast pick-a-time flow. The longer
 * qualifying intake lives at /get-started. UTM params on the URL are forwarded
 * into the booking's metadata so outreach attribution reaches the CRM.
 */
import { BookingWidget } from '@/components/BookingWidget'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'

function useUtmMetadata(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const meta: Record<string, string> = { source_page: '/book' }
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = params.get(key)
    if (value) meta[key] = value
  }
  return meta
}

export default function BookPage() {
  const breadcrumbs = generateBreadcrumbs('/book', breadcrumbLabels)
  const metadata = useUtmMetadata()

  useSEO({
    title: 'Book a Call',
    description: 'Grab a free 30-minute call to talk through your organization and whether Fugue Lab is a fit.',
    url: '/book',
    canonical: '/book',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Book a Call with Fugue Lab',
      description: "Schedule a free consultation to discuss your music organization's digital needs.",
      mainEntity: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        email: 'hello@fuguelab.com',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="bg-background min-h-screen py-16 sm:py-24">
      <div className="mx-auto mb-8 max-w-2xl px-4 text-center">
        <h1 className="text-foreground text-3xl font-bold tracking-tight md:text-4xl">Book a call</h1>
        <p className="text-muted-foreground mt-3 text-lg">
          Pick a time that works. Thirty minutes on Google Meet, looking at your site together. You'll leave with next
          steps, whether or not you hire me.
        </p>
      </div>
      <div className="px-4">
        <BookingWidget metadata={metadata} />
      </div>
    </div>
  )
}
